import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Box, Grid, Card, IconButton, Typography } from "@mui/material";
import { Bar } from "react-chartjs-2";
import { ArrowBack } from "@mui/icons-material";

import Table from "../../../common/Table";
import Button from "../../../common/Button";
import DataCard from "../DataCard";
import Loading from "../../../common/Loading";
import { exportToCSV } from "../../../../helpers";
import { useOrganisation } from "../../../../hooks/useOrganisation";
import {
  getAllCampaignClickedStatistics,
  getAllCampaignCreatedStatistics,
  getCampaignAdsStatistics,
  getCampaignAdsUsersStatistics,
  getTopCampaignStatistics,
} from "../../../../store/api/statistics";
import { articleColumns, campaignColumns, userColumns } from "./column";

const AdsTab = ({ startDate, endDate, organisation }) => {
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [selectedBarIndex, setSelectedBarIndex] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState("clickCount");
  const [sortOrder, setSortOrder] = useState("desc");
  const [activeView, setActiveView] = useState("clicked"); // 'created' or 'clicked'
  const { primaryColor } = useOrganisation();

  // Fetch all campaigns data (created)
  const {
    data: campaignsCreatedData,
    isLoading: loadingCreatedCampaigns,
    isFetching: fetchingCreatedCampaigns,
  } = useQuery({
    queryKey: [
      "allAdsCampaigns",
      startDate,
      endDate,
      organisation,
      currentPage,
      sortBy,
      sortOrder,
    ],
    queryFn: () =>
      getAllCampaignCreatedStatistics({
        startDate,
        endDate,
        organisationId: organisation || "",
        pageNumber: currentPage.toString(),
        sortBy,
        sortOrder,
      }),
    enabled: activeView === "created" && !selectedCampaign && !selectedArticle,
  });

  // Fetch all clicked campaigns data
  const {
    data: campaignsClickedData,
    isLoading: loadingClickedCampaigns,
    isFetching: fetchingClickedCampaigns,
  } = useQuery({
    queryKey: [
      "allClickedCampaigns",
      startDate,
      endDate,
      organisation,
      currentPage,
      sortBy,
      sortOrder,
    ],
    queryFn: () =>
      getAllCampaignClickedStatistics({
        startDate,
        endDate,
        organisationId: organisation || "",
        pageNumber: currentPage.toString(),
        sortBy,
        sortOrder,
      }),
    enabled: activeView === "clicked" && !selectedCampaign && !selectedArticle,
  });

  // Fetch top campaigns data
  const {
    data: topCampaignsData,
    isLoading: loadingTopCampaigns,
    isFetching: fetchingTopCampaigns,
  } = useQuery({
    queryKey: ["topAdsCampaigns", startDate, endDate, organisation],
    queryFn: () =>
      getTopCampaignStatistics({
        startDate,
        endDate,
        organisationId: organisation || "",
        pageNumber: "1",
        sortBy: "count",
        sortOrder: "desc",
      }),
  });

  // Fetch articles for selected campaign
  const {
    data: articlesData,
    isLoading: loadingArticles,
    isFetching: fetchingArticles,
  } = useQuery({
    queryKey: ["campaignArticles", selectedCampaign?.id, startDate, endDate],
    queryFn: () =>
      getCampaignAdsStatistics({
        campaignId: selectedCampaign?.id,
        startDate,
        endDate,
        organisationId: organisation || "",
        pageNumber: "1",
        sortBy: "clicks",
        sortOrder: "desc",
      }),
    enabled: !!selectedCampaign?.id,
  });

  // Fetch users who clicked the selected article
  const {
    data: articleUsersData,
    isLoading: loadingArticleUsers,
    isFetching: fetchingArticleUsers,
  } = useQuery({
    queryKey: ["articleUsers", selectedArticle?.id, startDate, endDate],
    queryFn: () =>
      getCampaignAdsUsersStatistics({
        addID: selectedArticle?.id,
        startDate,
        endDate,
        organisationId: organisation || "",
        pageNumber: "1",
        sortBy: "clicks",
        sortOrder: "desc",
      }),
    enabled: !!selectedArticle?.id,
  });

  const campaignsData =
    activeView === "created" ? campaignsCreatedData : campaignsClickedData;
  const loadingCampaigns =
    activeView === "created"
      ? loadingCreatedCampaigns
      : loadingClickedCampaigns;
  const fetchingCampaigns =
    activeView === "created"
      ? fetchingCreatedCampaigns
      : fetchingClickedCampaigns;

  const cardsData = [
    {
      title: "Created Campaigns",
      key: "campaigns",
      count: topCampaignsData?.createdAds || 0,
      isActive:
        activeView === "created" && !selectedCampaign && !selectedArticle,
      isSelected: "created",
    },
    {
      title: "Total Clicks",
      key: "clicks",
      count: topCampaignsData?.totalClicks || 0,
      isActive:
        activeView === "clicked" && !selectedCampaign && !selectedArticle,
      isSelected: "clicked",
    },
    {
      title: "Avg CTR",
      key: "ctr",
      count: topCampaignsData?.avgClick?.toFixed(2) + "%" || "0%",
      isActive: false,
      unHover: true,
    },
  ];

  const chartData = {
    labels: (topCampaignsData?.topAds || [])
      .slice(0, 3)
      .map((campaign) => campaign.name),
    datasets: [
      {
        label: "Campaign Clicks",
        data: (topCampaignsData?.topAds || [])
          .slice(0, 3)
          .map((campaign) => campaign.count),
        borderWidth: 1,
        backgroundColor: Array(3)
          .fill()
          .map((_, index) =>
            index === selectedBarIndex ? primaryColor : "grey"
          ),
      },
    ],
  };

  // Chart options
  const chartOptions = {
    scales: {
      y: { beginAtZero: true },
      x: {
        categoryPercentage: 0.6,
        barPercentage: 0.5,
      },
    },
    responsive: true,
    onClick: (_, elements) => {
      if (elements.length > 0) {
        const dataIndex = elements[0].index;
        const campaignData = (topCampaignsData?.topAds || []).slice(0, 3)[
          dataIndex
        ];

        setSelectedBarIndex(dataIndex);
        setSelectedCampaign({
          id: campaignData.id,
          title: campaignData.name,
        });
        setSelectedArticle(null);
        setCurrentPage(1);
      }
    },
    onHover: (event, chartElements) => {
      event.native.target.style.cursor = chartElements.length
        ? "pointer"
        : "default";
    },
  };

  const handleSort = (columnId) => {
    if (sortBy === columnId.toLowerCase()) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(columnId.toLowerCase());
      setSortOrder("asc");
    }
    setCurrentPage(1); // Reset to first page when sorting changes
  };

  const handleBackClick = () => {
    if (selectedArticle) {
      setSelectedArticle(null);
    } else if (selectedCampaign) {
      setSelectedCampaign(null);
      setSelectedBarIndex(null);
    }
  };

  // Handle clicking on a campaign card
  const handleCardClick = (cardKey) => {
    setSelectedCampaign(null);
    setSelectedArticle(null);
    setSelectedBarIndex(null);
    setCurrentPage(1);

    if (cardKey === "campaigns") {
      setActiveView("created");
      setSortBy("clickCount");
      setSortOrder("desc");
    } else if (cardKey === "clicks") {
      // When clicking on the Clicks card, show all clicked campaigns
      setActiveView("clicked");
      setSortBy("clickCount");
      setSortOrder("desc");
    }
  };

  // Handle clicking on a campaign in the table
  const handleCampaignClick = (campaign) => {
    // Prevent clicking if it's a created campaign with 0 clicks
    if (
      activeView === "created" &&
      (!campaign.clickCount || campaign.clickCount === 0)
    ) {
      return; // Do nothing if no clicks
    }

    setSelectedCampaign({
      id: campaign.adId,
      title: campaign.name,
    });
    setSelectedArticle(null);
    setCurrentPage(1);
  };

  // Handle clicking on an article in the table
  const handleArticleClick = (article) => {
    setSelectedArticle({
      id: article.adId,
      title: article.name,
    });
    setCurrentPage(1);
  };

  if (
    (loadingTopCampaigns && !topCampaignsData) ||
    (loadingCampaigns &&
      !campaignsData &&
      !selectedCampaign &&
      !selectedArticle)
  ) {
    return <Loading />;
  }

  return (
    <Box sx={{ maxWidth: "1200px", mx: "auto", px: { xs: 2, sm: 3, md: 4 } }}>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ position: "relative" }}>
            {loadingTopCampaigns ? (
              <Box
                sx={{
                  p: 3,
                  textAlign: "center",
                  height: 300,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Loading />
              </Box>
            ) : topCampaignsData?.topAds?.length > 0 ? (
              <>
                <Bar data={chartData} options={chartOptions} />
                {fetchingTopCampaigns && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: "rgba(255, 255, 255, 0.7)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: 1,
                    }}
                  >
                    <Loading />
                  </Box>
                )}
              </>
            ) : (
              <Box sx={{ p: 3, textAlign: "center" }}>
                <Typography>No campaign data available</Typography>
              </Box>
            )}
          </Card>
        </Grid>

        {/* Data Cards */}
        <Grid item xs={12} md={6}>
          <Grid container spacing={2}>
            {cardsData.map((cardData) => (
              <Grid item xs={6} key={cardData.key}>
                <DataCard
                  title={cardData.title}
                  count={loadingTopCampaigns ? "..." : cardData.count}
                  isActivated={cardData.isActive}
                  isSelected={cardData.isSelected}
                  onClick={() =>
                    !loadingTopCampaigns && handleCardClick(cardData.key)
                  }
                  sx={{
                    cursor: loadingTopCampaigns ? "default" : "pointer",
                    opacity: loadingTopCampaigns ? 0.7 : 1,
                    transition: "opacity 0.2s ease",
                  }}
                  unHover={cardData.unHover || loadingTopCampaigns}
                />
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>

      {selectedArticle ? (
        <>
          <Box sx={{ display: "flex", justifyContent: "space-between", my: 2 }}>
            <Box sx={{ mb: 3, display: "flex", alignItems: "center" }}>
              <IconButton
                onClick={handleBackClick}
                sx={{ mr: 2 }}
                disabled={loadingArticleUsers}
              >
                <ArrowBack />
              </IconButton>
              <Typography variant="h6">
                Users who clicked: {selectedArticle.title}
              </Typography>
            </Box>
            <Button
              text="Export CSV"
              onClick={() =>
                exportToCSV(articleUsersData?.paginatedContent || [])
              }
              disabled={
                loadingArticleUsers ||
                !articleUsersData?.paginatedContent?.length
              }
            />
          </Box>
          <Table
            data={articleUsersData?.paginatedContent || []}
            columns={userColumns({
              onSort: handleSort,
              sortBy,
              sortOrder,
            })}
            totalPage={articleUsersData?.totalPages || 1}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            isLoading={fetchingArticleUsers}
            isInitialLoading={loadingArticleUsers}
          />
        </>
      ) : selectedCampaign ? (
        <>
          <Box sx={{ display: "flex", justifyContent: "space-between", my: 2 }}>
            <Box sx={{ mb: 3, display: "flex", alignItems: "center" }}>
              <IconButton
                onClick={handleBackClick}
                sx={{ mr: 2 }}
                disabled={loadingArticles}
              >
                <ArrowBack />
              </IconButton>
              <Typography variant="h6">
                Articles for Campaign: {selectedCampaign.title}
              </Typography>
            </Box>
            <Button
              text="Export CSV"
              onClick={() => exportToCSV(articlesData?.paginatedContent || [])}
              disabled={
                loadingArticles || !articlesData?.paginatedContent?.length
              }
            />
          </Box>
          <Table
            data={articlesData?.paginatedContent || []}
            columns={articleColumns({
              onRowClick: handleArticleClick,
              onSort: handleSort,
              sortBy,
              sortOrder,
              primaryColor,
            })}
            totalPage={articlesData?.totalPages || 1}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            isLoading={fetchingArticles}
            isInitialLoading={loadingArticles}
          />
        </>
      ) : (
        <>
          <Box
            sx={{
              mt: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">
              {activeView === "created" ? "All Campaigns" : "Clicked Campaigns"}
            </Typography>
            <Button
              text="Export CSV"
              onClick={() => exportToCSV(campaignsData?.paginatedContent || [])}
              disabled={
                loadingCampaigns || !campaignsData?.paginatedContent?.length
              }
            />
          </Box>
          <Table
            data={campaignsData?.paginatedContent || []}
            columns={campaignColumns({
              onRowClick: handleCampaignClick,
              onSort: handleSort,
              sortBy,
              sortOrder,
              primaryColor,
              activeView, // Pass activeView to determine styling
            })}
            totalPage={campaignsData?.totalPages || 1}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            onSort={handleSort}
            sortBy={sortBy}
            sortOrder={sortOrder}
            isLoading={fetchingCampaigns}
            isInitialLoading={loadingCampaigns}
          />
        </>
      )}
    </Box>
  );
};

export default AdsTab;
